import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import {
  DemandTypeFilter,
  DEMAND_TYPE_LABELS,
} from "@/components/features/reports/Demands/DemandTypeFilter";
import {
  DEMAND_TYPES,
  type DemandType,
} from "@/lib/data/requests/reports/reports-matter-demand-report";
import { DemandReportSummaryCards } from "@/components/features/reports/Demands/DemandReportSummaryCards";
import { isAxiosError } from "axios";
import type { APIError } from "@/lib/types";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { ReportUserFilter } from "@/components/features/reports/ReportUserFilter";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { useUser } from "@/components/UserProvider";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  Badge,
  CurrencyWrapper,
  DataTable,
  DataTableColumnHeader,
  DateFormField,
  Form,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import {
  getUsersOptions,
  retrieveMatterSettingsQueryOptions,
  retrieveReportsMatterDemandReportQueryOptions,
} from "@/lib/data/react-query";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type ReportsMatterDemandReport } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import {
  REPORT_DATE_RANGE_OPTIONS,
  REPORT_DATE_RANGE_SEARCH_VALUES,
  type ReportDateRangeType,
  formatResolvedReportDateRangeLabel,
  isReportDateRangeType,
  resolveReportDateRange,
} from "@/lib/utils/reports/report-filter-options";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Handshake,
  type LucideIcon,
  Wallet,
} from "lucide-react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/demands")({
  component: RouteComponent,
  validateSearch: z.object({
    dateRangeEnd: z.string().optional(),
    dateRangeStart: z.string().optional(),
    dateRangeType: z.enum(REPORT_DATE_RANGE_SEARCH_VALUES).optional(),
    employee: z.string().optional(),
    team: z.string().optional(),
    type: z.string().optional(),
    claimType: z.string().optional(),
    demandType: z.string().optional(),
  }),
});

const DEMAND_REPORT_TABLE_NAME = tanstackTableNames.reportsMatterDemand;
const DEMAND_REPORT_PRESET_COLUMN_IDS = {
  assignees: "Assignees",
  dateExpires: "Expiration Date",
  dateSent: "Date Sent",
  demandAmount: "Demand Amount",
  demandCategory: "Demand Category",
  matter: "Matter",
  matterType: "Incident Type",
  claimTypes: "Claim Types",
  policy: "Policy",
  team: "Team",
  totalDemandAmount: "Total Demand Amount",
  totalMeds: "Total Meds",
} as const;
const DEMAND_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(DEMAND_REPORT_PRESET_COLUMN_IDS).map(([key, value]) => [
    value,
    key,
  ]),
) as Record<string, keyof typeof DEMAND_REPORT_PRESET_COLUMN_IDS>;
const DEMAND_REPORT_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type DemandReportPresetFilters = {
  dateRangeEnd?: string;
  dateRangeStart?: string;
  dateRangeType?: string;
  demandTypes?: DemandType[];
  employee?: string[];
  team?: string[];
  type?: string[];
};

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const filters = rawFilters as {
    claimTypeIds?: string[];
    matterTypeIds?: string[];
  } & DemandReportPresetFilters;
  const dateRangeType = isReportDateRangeType(filters.dateRangeType)
    ? filters.dateRangeType
    : "ALL_TIME";

  return {
    claimType: serializeSearchValues(
      normalizePresetStringArray(filters.claimTypeIds),
    ),
    dateRangeEnd:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeEnd === "string"
        ? filters.dateRangeEnd
        : undefined,
    dateRangeStart:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeStart === "string"
        ? filters.dateRangeStart
        : undefined,
    dateRangeType,
    demandType: serializeSearchValues(
      normalizePresetStringArray(filters.demandTypes),
    ),
    employee: serializeSearchValues(
      normalizePresetStringArray(filters.employee),
    ),
    team: serializeSearchValues(normalizePresetStringArray(filters.team)),
    type: serializeSearchValues(normalizePresetStringArray(filters.type)),
  };
};

const addUniqueValue = (values: string[], value: string) =>
  values.includes(value) ? values : [...values, value];

const getUniqueTrimmedValues = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(values.map((value) => value?.trim() ?? "").filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right));

const formatDateValue = (value?: string | null) =>
  formatDate(value ?? undefined) ?? "-";

const normalizeNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }
  return undefined;
};

const isDemandExpirationPassed = (expirationDate?: string | null) =>
  Boolean(
    expirationDate &&
      dayjs(expirationDate).isValid() &&
      dayjs(expirationDate).startOf("day").isBefore(dayjs().startOf("day")),
  );

const getDemandCategory = (report: ReportsMatterDemandReport) => {
  if (report.thirdParty && !report.firstParty) {
    return "3rd Party";
  }

  const coverageType = report.coverageType?.trim();
  const normalizedCoverageType = coverageType?.toLowerCase();

  if (normalizedCoverageType === "um") {
    return "UM";
  }
  if (normalizedCoverageType === "uim") {
    return "UIM";
  }
  if (
    normalizedCoverageType === "medpay" ||
    normalizedCoverageType === "med pay"
  ) {
    return "MedPay";
  }
  if (report.firstParty) {
    return "1st Party";
  }

  return "-";
};

const getPolicyLabel = (report: ReportsMatterDemandReport) => {
  const parts: string[] = [];
  const insuranceType = report.insuranceType?.trim();
  const limitPerPerson = normalizeNumber(report.policyLimitPerPerson);
  const limitPerIncident = normalizeNumber(report.policyLimitPerIncident);

  if (insuranceType) {
    parts.push(insuranceType);
  }
  if (report.policyLimit) {
    parts.push("Policy limits");
  }
  if (limitPerPerson || limitPerIncident) {
    const limitParts: string[] = [];
    if (limitPerPerson) {
      limitParts.push(
        `PP ${DEMAND_REPORT_CURRENCY_FORMATTER.format(limitPerPerson)}`,
      );
    }
    if (limitPerIncident) {
      limitParts.push(
        `PI ${DEMAND_REPORT_CURRENCY_FORMATTER.format(limitPerIncident)}`,
      );
    }
    parts.push(limitParts.join(" / "));
  }

  return parts.join(" • ") || "-";
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { hasPagePermission } = useUser();

  const canViewDemandReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsDemands,
  );
  const canViewMatterDetails = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetail,
  );

  const selectedDateRange: ReportDateRangeType =
    search.dateRangeType ?? "ALL_TIME";
  const dateRangeStart = search.dateRangeStart;
  const dateRangeEnd = search.dateRangeEnd;
  const dateRangeForm = useForm<{
    dateRangeEnd: string;
    dateRangeStart: string;
  }>({
    defaultValues: {
      dateRangeEnd: dateRangeEnd ?? "",
      dateRangeStart: dateRangeStart ?? "",
    },
  });
  const watchedDateRangeStart = dateRangeForm.watch("dateRangeStart");
  const watchedDateRangeEnd = dateRangeForm.watch("dateRangeEnd");

  const [selectedEmployees, setSelectedEmployees] = useState<string[]>(() =>
    parseSearchValues(search.employee),
  );
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() =>
    parseSearchValues(search.team),
  );
  const selectedClaimTypeIds = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const selectedDemandTypes = useMemo(
    () =>
      parseSearchValues(search.demandType).filter(
        (value): value is DemandType =>
          DEMAND_TYPES.includes(value as DemandType),
      ),
    [search.demandType],
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    parseSearchValues(search.type),
  );
  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();
  const [typeSelectValue, setTypeSelectValue] = useState<string | undefined>();

  useEffect(() => {
    setSelectedEmployees(parseSearchValues(search.employee));
  }, [search.employee]);
  useEffect(() => {
    setSelectedTeams(parseSearchValues(search.team));
  }, [search.team]);
  useEffect(() => {
    setSelectedTypes(parseSearchValues(search.type));
  }, [search.type]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    if (dateRangeStart || dateRangeEnd) {
      return;
    }
    dateRangeForm.reset(
      { dateRangeEnd: "", dateRangeStart: "" },
      { keepDefaultValues: true },
    );
  }, [dateRangeEnd, dateRangeForm, dateRangeStart, selectedDateRange]);
  useEffect(() => {
    dateRangeForm.setValue("dateRangeStart", dateRangeStart ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    dateRangeForm.setValue("dateRangeEnd", dateRangeEnd ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dateRangeEnd, dateRangeForm, dateRangeStart]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    const nextStart = watchedDateRangeStart.trim();
    const nextEnd = watchedDateRangeEnd.trim();
    if (!nextStart || !nextEnd) {
      return;
    }
    if (nextStart === dateRangeStart && nextEnd === dateRangeEnd) {
      return;
    }
    void navigate({
      search: (prev) => ({
        ...prev,
        dateRangeEnd: nextEnd,
        dateRangeStart: nextStart,
      }),
      replace: true,
    });
  }, [
    dateRangeEnd,
    dateRangeStart,
    navigate,
    selectedDateRange,
    watchedDateRangeEnd,
    watchedDateRangeStart,
  ]);

  const resolvedDateRange = useMemo(
    () =>
      resolveReportDateRange({
        dateRangeEnd,
        dateRangeStart,
        dateRangeType: selectedDateRange,
      }),
    [dateRangeEnd, dateRangeStart, selectedDateRange],
  );
  const effectiveDateRangeType = useMemo<ReportDateRangeType>(
    () =>
      selectedDateRange === "CUSTOM_DATE_RANGE" &&
      (!resolvedDateRange.start || !resolvedDateRange.end)
        ? "ALL_TIME"
        : selectedDateRange,
    [resolvedDateRange.end, resolvedDateRange.start, selectedDateRange],
  );
  const hasActiveDateRange = Boolean(
    effectiveDateRangeType !== "ALL_TIME" &&
      (effectiveDateRangeType !== "CUSTOM_DATE_RANGE" ||
        (resolvedDateRange.start && resolvedDateRange.end)),
  );
  const hasActiveFilters =
    hasActiveDateRange ||
    selectedEmployees.length > 0 ||
    selectedTeams.length > 0 ||
    selectedTypes.length > 0 ||
    selectedDemandTypes.length > 0;

  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    ...getUsersOptions(),
    enabled: canViewDemandReport,
  });
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewDemandReport,
  });

  const reportFilters = useMemo(() => {
    return {
      assigneeUserIds: selectedEmployees,
      demandTypes: selectedDemandTypes,
      dateRange: {
        preset: effectiveDateRangeType,
        ...(effectiveDateRangeType === "CUSTOM_DATE_RANGE"
          ? {
              endDate: resolvedDateRange.end,
              startDate: resolvedDateRange.start,
            }
          : {}),
      },
      incidentTypeIds: selectedTypes,
      claimTypeIds: selectedClaimTypeIds,
      teamIds: selectedTeams,
    };
  }, [
    effectiveDateRangeType,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedEmployees,
    selectedTeams,
    selectedTypes,
    selectedClaimTypeIds,
    selectedDemandTypes,
  ]);

  const { data, error, isLoading } = useQuery({
    ...retrieveReportsMatterDemandReportQueryOptions(reportFilters),
    enabled: canViewDemandReport,
  });

  const report = data?.data;
  const reports = useMemo<ReportsMatterDemandReport[]>(
    () => report?.rows ?? [],
    [report?.rows],
  );
  const metrics = error ? undefined : report?.metrics;
  const errorMessage = isAxiosError<Partial<APIError>>(error)
    ? error.response?.data.meta?.userMsg
    : undefined;

  const teamOptions = useMemo(() => {
    const teams = matterSettingsData?.data.teams ?? [];
    const options = teams.map((team) => ({
      value: team.id,
      label: team.label,
    }));
    const existing = new Set(options.map((option) => option.value));

    selectedTeams.forEach((id) => {
      if (!existing.has(id)) {
        options.push({ value: id, label: id });
      }
    });

    return options.sort((left, right) => left.label.localeCompare(right.label));
  }, [matterSettingsData?.data.teams, selectedTeams]);

  const typeOptions = useMemo(() => {
    const types = matterSettingsData?.data.incidentTypes ?? [];
    const options = types.map((type) => ({
      value: type.id,
      label: getIncidentTypeLabel(type),
    }));
    const existing = new Set(options.map((option) => option.value));

    selectedTypes.forEach((id) => {
      if (!existing.has(id)) {
        options.push({ value: id, label: id });
      }
    });

    return options.sort((left, right) => left.label.localeCompare(right.label));
  }, [matterSettingsData?.data.incidentTypes, selectedTypes]);

  const userDirectory = useUserDirectory({
    enabled: canViewDemandReport,
    selectedIds: selectedEmployees,
  });

  const employeeOptions = useMemo(() => {
    const users = userDirectory.users;
    const mappedUsers = users
      .map((user) => {
        const nameParts = [user.firstName.trim(), user.lastName.trim()].filter(
          Boolean,
        );
        const label = nameParts.join(" ");
        return {
          value: user.id,
          label: label || user.id,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));

    const options = [...mappedUsers];
    const existing = new Set(options.map((option) => option.value));
    selectedEmployees.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [selectedEmployees, userDirectory.users]);

  const resolveOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string,
  ) => options.find((option) => option.value === value)?.label ?? value;

  const claimFilterChips = selectedClaimTypeIds.map((id) => ({
    key: `claimType:${id}`,
    label: `Claim type: ${(matterSettingsData?.data.claimTypes ?? []).find((claimType) => claimType.id === id)?.label ?? id}`,
    onRemove: () => {
      void navigate({
        search: (prev) => ({
          ...prev,
          claimType: serializeSearchValues(
            selectedClaimTypeIds.filter((value) => value !== id),
          ),
        }),
        replace: true,
      });
    },
  }));
  const demandTypeFilterChips = selectedDemandTypes.map((value) => ({
    key: `demandType:${value}`,
    label: `Demand Type: ${DEMAND_TYPE_LABELS[value]}`,
    onRemove: () => {
      void navigate({
        search: (prev) => ({
          ...prev,
          demandType: serializeSearchValues(
            selectedDemandTypes.filter((item) => item !== value),
          ),
        }),
        replace: true,
      });
    },
  }));
  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (hasActiveDateRange) {
      const label =
        formatResolvedReportDateRangeLabel({
          dateRangeEnd: resolvedDateRange.end,
          dateRangeStart: resolvedDateRange.start,
          dateRangeType: effectiveDateRangeType,
        }) ?? effectiveDateRangeType;
      chips.push({
        key: `dateRange:${resolvedDateRange.start ?? ""}-${resolvedDateRange.end ?? ""}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              dateRangeType: "ALL_TIME",
            }),
            replace: true,
          });
        },
      });
    }

    selectedEmployees.forEach((value) => {
      chips.push({
        key: `employee:${value}`,
        label: `Assignee: ${resolveOptionLabel(employeeOptions, value)}`,
        onRemove: () => {
          const next = selectedEmployees.filter((item) => item !== value);
          setSelectedEmployees(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              employee: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          const next = selectedTeams.filter((item) => item !== value);
          setSelectedTeams(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedTypes.forEach((value) => {
      chips.push({
        key: `type:${value}`,
        label: `Incident type: ${resolveOptionLabel(typeOptions, value)}`,
        onRemove: () => {
          const next = selectedTypes.filter((item) => item !== value);
          setSelectedTypes(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              type: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    effectiveDateRangeType,
    employeeOptions,
    hasActiveDateRange,
    navigate,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedEmployees,
    selectedTeams,
    selectedTypes,
    teamOptions,
    typeOptions,
  ]);

  const summaryCards: Array<{
    format: "count" | "currency";
    icon: LucideIcon;
    label: string;
    value?: number;
  }> = [
    {
      format: "count",
      icon: Handshake,
      label: "Total demands sent",
      value: metrics?.totalDemandSent,
    },
    {
      format: "currency",
      icon: Wallet,
      label: "Total demanded",
      value: metrics?.totalDemandAmount,
    },
    {
      format: "currency",
      icon: AlertCircle,
      label: "Third-party demands",
      value: metrics?.thirdPartyDemandAmount,
    },
    {
      format: "currency",
      icon: CheckCircle2,
      label: "UM demands",
      value: metrics?.UmDemandAmount,
    },
    {
      format: "currency",
      icon: Clock,
      label: "UIM demands",
      value: metrics?.UimDemandAmount,
    },
    {
      format: "currency",
      icon: Wallet,
      label: "MedPay demands",
      value: metrics?.MedpayDemandAmount,
    },
  ];

  const demandColumns = useMemo<ColumnDef<ReportsMatterDemandReport>[]>(
    () => [
      {
        accessorKey: "matterTitle",
        id: "Matter",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.matterTitle ?? "-",
        enableHiding: false,
      },
      {
        accessorFn: (row) => row.dateSent,
        id: "Date Sent",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => formatDateValue(row.original.dateSent),
        sortingFn: "customDateTime",
      },
      {
        accessorFn: (row) => row.dateExpires,
        id: "Expiration Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const expirationDate = row.original.dateExpires;

          return (
            <span
              className={
                isDemandExpirationPassed(expirationDate)
                  ? "text-destructive"
                  : undefined
              }
            >
              {formatDateValue(expirationDate)}
            </span>
          );
        },
        sortingFn: "customDateTime",
      },
      {
        accessorFn: (row) => normalizeNumber(row.amount),
        id: "Demand Amount",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper value={row.original.amount ?? undefined} showCents />
        ),
      },
      {
        accessorKey: "teams",
        id: "Team",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const teams = getUniqueTrimmedValues(
            (row.original.teams ?? []).map((team) => team.label),
          );
          return <MultiValueCell value={teams} />;
        },
      },
      {
        accessorKey: "assigneeUserIds",
        id: "Assignees",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          if (isUsersLoading) {
            return (
              <Badge className="bg-muted text-muted h-4 w-12 animate-pulse text-transparent">
                Loading
              </Badge>
            );
          }

          const ids = getUniqueTrimmedValues(
            row.original.assigneeUserIds ?? [],
          );
          if (ids.length === 0) {
            return "-";
          }

          const users = usersData?.data ?? [];
          const labels = ids.map((id) => {
            const user = users.find((item) => item.id === id);
            if (!user) {
              return id;
            }
            return [user.firstName.trim(), user.lastName.trim()]
              .filter(Boolean)
              .join(" ");
          });

          return <MultiValueCell value={labels} />;
        },
      },
      {
        accessorKey: "incidentTypeLabel",
        id: "Incident Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.incidentTypeLabel ?? "-",
      },
      {
        accessorFn: (row) =>
          (row.claimTypes ?? []).map((claimType) => claimType.label).join(", "),
        id: "Claim Types",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <ClaimTypesCell claimTypes={row.original.claimTypes} />
        ),
      },
      {
        accessorFn: (row) => getDemandCategory(row),
        id: "Demand Category",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const category = getDemandCategory(row.original);
          return category === "-" ? (
            "-"
          ) : (
            <Badge
              className="bg-muted text-foreground border-border hover:bg-muted/80 px-2.5 py-0.5 text-xs font-semibold"
              variant="outline"
            >
              {category}
            </Badge>
          );
        },
      },
      {
        accessorFn: (row) => normalizeNumber(row.amount),
        id: "Total Demand Amount",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={normalizeNumber(row.original.amount)}
            showCents
          />
        ),
      },
      {
        accessorFn: (row) => row.originalMedicalBillTotal,
        id: "Total Meds",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={row.original.originalMedicalBillTotal ?? undefined}
            showCents
          />
        ),
      },
      {
        accessorFn: (row) => getPolicyLabel(row),
        id: "Policy",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => getPolicyLabel(row.original),
      },
    ],
    [isUsersLoading, usersData?.data],
  );

  const demandPresetColumnKeysById = useMemo(
    () =>
      demandColumns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") {
          return acc;
        }
        if (!(column.id in DEMAND_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] = DEMAND_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [demandColumns],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: demandColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      claimTypeIds: selectedClaimTypeIds,
      demandTypes: selectedDemandTypes,
      dateRangeEnd:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.end
          : undefined,
      dateRangeStart:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.start
          : undefined,
      dateRangeType: selectedDateRange,
      employee: selectedEmployees,
      team: selectedTeams,
      type: selectedTypes,
    },
    enabled: canViewDemandReport,
    lockedVisibleColumnIds: [DEMAND_REPORT_PRESET_COLUMN_IDS.matter],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: demandPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.matterDemand,
    tableName: DEMAND_REPORT_TABLE_NAME,
  });

  if (!canViewDemandReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Demands"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={[
            ...appliedFilterChips,
            ...claimFilterChips,
            ...demandTypeFilterChips,
          ].map((chip) => toReportPresetPreviewFilter(chip.label))}
          saveTitle="Save demands report preset"
          selectId="demands-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[
          ...appliedFilterChips,
          ...claimFilterChips,
          ...demandTypeFilterChips,
        ].map((chip) => ({
          ...chip,
          onRemove: () => {
            clearSelectedPreset();
            chip.onRemove();
          },
        }))}
        onClearFilters={async () => {
          clearSelectedPreset();
          dateRangeForm.reset(
            { dateRangeEnd: "", dateRangeStart: "" },
            { keepDefaultValues: true },
          );
          setSelectedEmployees([]);
          setSelectedTeams([]);
          setSelectedTypes([]);
          setTeamSelectValue(undefined);
          setTypeSelectValue(undefined);
          await navigate({
            search: () => ({
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              dateRangeType: "ALL_TIME",
              employee: undefined,
              team: undefined,
              type: undefined,
              claimType: undefined,
              demandType: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              const nextValue = value as ReportDateRangeType;
              await navigate({
                search: (prev) => ({
                  ...prev,
                  dateRangeEnd:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeEnd
                      : undefined,
                  dateRangeStart:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeStart
                      : undefined,
                  dateRangeType: nextValue,
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {REPORT_DATE_RANGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTeams, value);
              setSelectedTeams(next);
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Teams" />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportUserFilter
            {...userDirectory.selectProps}
            items={employeeOptions}
            onChange={async (value) => {
              const next = addUniqueValue(selectedEmployees, value);
              setSelectedEmployees(next);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  employee: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          />
          <ClaimTypeFilter
            value={selectedClaimTypeIds}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(value),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={typeSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTypes, value);
              setSelectedTypes(next);
              setTypeSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  type: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Incident types" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DemandTypeFilter
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  demandType: serializeSearchValues([
                    ...new Set([...selectedDemandTypes, value]),
                  ]),
                }),
                replace: true,
              });
            }}
          />
          {selectedDateRange === "CUSTOM_DATE_RANGE" && (
            <Form {...dateRangeForm}>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-2">
                <DateFormField
                  clearable={false}
                  label="Start date"
                  name="dateRangeStart"
                  register={dateRangeForm.register("dateRangeStart")}
                />
                <DateFormField
                  clearable={false}
                  label="End date"
                  name="dateRangeEnd"
                  register={dateRangeForm.register("dateRangeEnd")}
                />
              </div>
            </Form>
          )}
        </div>
      </ReportFilterBar>
      <DemandReportSummaryCards
        summaryCards={summaryCards}
        isLoading={isLoading}
      />
      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {errorMessage ?? "Unable to load demands. Please try again."}
        </p>
      ) : (
        <DataTable
          entityLabelPlural="demands"
          columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
          newColumnAnchors={REPORT_CLASSIFICATION_COLUMN_ANCHORS}
          key={tableRenderKey}
          hideSearchBar
          columns={demandColumns}
          data={reports}
          initialSort={{ id: "Date Sent", desc: true }}
          isError={Boolean(error)}
          isLoading={isLoading || isUsersLoading}
          leftToolbarContent={
            <Text variant="body" className="text-lg font-semibold">
              Demand details
            </Text>
          }
          noResultsMessage={
            hasActiveFilters
              ? "No demands found for the selected filters."
              : "No demands found."
          }
          onColumnVisibilityChange={handleColumnVisibilityChange}
          renderRowContextMenu={(row, trigger) => {
            const matterId = row.original.matterId;
            return canViewMatterDetails && matterId ? (
              <MatterRowContextMenu matterId={matterId} trigger={trigger} />
            ) : (
              trigger
            );
          }}
          onRowClick={async (row) => {
            const matterId = row.original.matterId;
            if (!canViewMatterDetails || !matterId) {
              return;
            }
            await navigate({
              to: "/matters/$matterId",
              params: { matterId },
            });
          }}
          tableName={DEMAND_REPORT_TABLE_NAME}
        />
      )}
    </ReportsPageLayout>
  );
}
